interface YearRange {
  startYear: string;
  endYear: string;
}

export const extractYearFromLabel = (label: string): number | null => {
  if (!label) return null;

  const pyMatch = label.match(/PY\s*(\d{2,4})/i);
  if (pyMatch) {
    const year = parseInt(pyMatch[1], 10);
    return year < 100 ? 2000 + year : year;
  }

  const yearMatch = label.match(/\b(20\d{2})\b/);
  if (yearMatch) {
    return parseInt(yearMatch[1], 10);
  }

  const shortYearMatch = label.match(/\b(\d{2})\b/);
  if (shortYearMatch) {
    const year = parseInt(shortYearMatch[1], 10);
    return year < 100 ? 2000 + year : year;
  }

  return null;
};

export const getYearRangeFromStackedBarData = (data: any[]): YearRange | null => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  const years: number[] = [];

  data.forEach((item) => {
    const year = extractYearFromLabel(item?.label);
    if (year) {
      years.push(year);
    }
  });

  if (years.length === 0) {
    return null;
  }

  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);

  return {
    startYear: minYear.toString(),
    endYear: maxYear.toString(),
  };
};

export const getYearRangeFromTableData = (tableData: any): YearRange | null => {
  const years: number[] = [];

  // First, try to extract from header row
  const headerRow = tableData?.header?.row;
  if (headerRow && Array.isArray(headerRow) && headerRow.length > 0) {
    headerRow.forEach((cell: any) => {
      const value = cell?.kpiValue || cell;
      if (typeof value === 'string') {
        const year = extractYearFromLabel(value);
        if (year) {
          years.push(year);
        }
      }
    });
  }

  // If no years found in header, try body rows (check first cell's rowComponent for year labels)
  if (years.length === 0) {
    const bodyRows = tableData?.body?.rows;
    if (bodyRows && Array.isArray(bodyRows)) {
      bodyRows.forEach((row: any[]) => {
        // Check rowComponent in first cell (nested rows with year labels like "PY 25($)")
        const rowComponent = row?.[0]?.rowComponent;
        if (rowComponent && Array.isArray(rowComponent)) {
          rowComponent.forEach((nestedRow: any[]) => {
            const firstCell = nestedRow?.[0];
            if (firstCell?.kpiValue && typeof firstCell.kpiValue === 'string') {
              const year = extractYearFromLabel(firstCell.kpiValue);
              if (year) {
                years.push(year);
              }
            }
          });
        }
        // Also check first cell directly for year patterns
        const firstCell = row?.[0];
        if (firstCell?.kpiValue && typeof firstCell.kpiValue === 'string') {
          const year = extractYearFromLabel(firstCell.kpiValue);
          if (year) {
            years.push(year);
          }
        }
      });
    }
  }

  if (years.length === 0) {
    return null;
  }

  // Remove duplicates and get min/max
  const uniqueYears = Array.from(new Set(years));
  const minYear = Math.min(...uniqueYears);
  const maxYear = Math.max(...uniqueYears);

  return {
    startYear: minYear.toString(),
    endYear: maxYear.toString(),
  };
};

export const getYearRange = (stackedBarData: any[] | null, tableData: any): YearRange | null => {
  const barYearRange = getYearRangeFromStackedBarData(stackedBarData || []);
  if (barYearRange) {
    return barYearRange;
  }

  return getYearRangeFromTableData(tableData);
};
